import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Code, Mail, Terminal, Database, Server, Bot, Cloud, ShieldCheck, Layout, Zap, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useCountUp } from '../hooks/useScrollAnimation';
import { BlogPost } from '../data/blogPosts';
import { fetchPosts } from '../services/blogService';

/* ───── tiny reusable animated wrapper ───── */
const AnimatedSection: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

/* ───── typing effect hook ───── */
const useTypingEffect = (words: string[], typeSpeed = 80, deleteSpeed = 40, pause = 2000, isActive = true) => {
  const [text, setText] = useState('');
  const [wordIdx, setWordIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isActive) return;
    const current = words[wordIdx];
    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          setText(current.slice(0, text.length + 1));
          if (text.length + 1 === current.length) setTimeout(() => setIsDeleting(true), pause);
        } else {
          setText(current.slice(0, text.length - 1));
          if (text.length - 1 === 0) {
            setIsDeleting(false);
            setWordIdx((prev) => (prev + 1) % words.length);
          }
        }
      },
      isDeleting ? deleteSpeed : typeSpeed,
    );
    return () => clearTimeout(timeout);
  }, [text, isDeleting, wordIdx, words, typeSpeed, deleteSpeed, pause, isActive]);

  return text;
};

/* ───── stat card ───── */
const StatCard: React.FC<{ value: number; suffix: string; label: string; delay: number }> = ({
  value,
  suffix,
  label,
  delay,
}) => {
  const { count, ref } = useCountUp(value, 1800);
  return (
    <div ref={ref} style={{ textAlign: 'center' }}>
      <AnimatedSection delay={delay}>
        <p
          style={{
            fontSize: 'clamp(1.8rem, 3vw, 2.5rem)',
            fontWeight: 800,
            fontFamily: 'var(--font-heading)',
            background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: 1.2,
          }}
        >
          {count}
          {suffix}
        </p>
        <p style={{ fontSize: '0.9rem', color: 'var(--color-gray-500)', marginTop: '4px', fontWeight: 500 }}>
          {label}
        </p>
      </AnimatedSection>
    </div>
  );
};

/* ───── skill bar ───── */
const SkillBar: React.FC<{
  name: string;
  level: number;
  icon: React.ReactNode;
  color: string;
  delay: number;
}> = ({ name, level, icon, color, delay }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <AnimatedSection delay={delay}>
      <div
        ref={ref}
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.05)',
          transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '12px',
              background: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            {icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--color-gray-800)' }}>{name}</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color }}>{level}%</span>
            </div>
          </div>
        </div>
        <div
          style={{
            height: '6px',
            background: 'var(--color-gray-100)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: isVisible ? `${level}%` : '0%',
              background: `linear-gradient(90deg, ${color}, ${color}aa)`,
              borderRadius: '3px',
              transition: 'width 1.2s cubic-bezier(.22,1,.36,1)',
            }}
          />
        </div>
      </div>
    </AnimatedSection>
  );
};

/* ═══════════════════════════════════════════ */
/*                  HOME PAGE                  */
/* ═══════════════════════════════════════════ */

const Home: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const [heroVisible, setHeroVisible] = useState(true);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => setHeroVisible(entry.isIntersecting), { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const typedText = useTypingEffect(
    ['Accessible to Every Business', 'Within Your Reach', 'Your Competitive Edge', 'Simple to Adopt'],
    90,
    50,
    2200,
    heroVisible,
  );

  const skills = [
    { name: 'Cloud Architecture', level: 95, icon: <Cloud size={20} />, color: '#FF9900' },
    { name: 'Generative AI', level: 95, icon: <Bot size={20} />, color: '#8B5CF6' },
    { name: 'Python', level: 95, icon: <Terminal size={20} />, color: '#3776AB' },
    { name: 'System Design', level: 90, icon: <Layout size={20} />, color: '#1E3A5F' },
    { name: 'Spring Boot', level: 85, icon: <Server size={20} />, color: '#6DB33F' },
    { name: 'Node.js', level: 85, icon: <Server size={20} />, color: '#339933' },
    { name: 'React', level: 90, icon: <Code size={20} />, color: '#61DAFB' },
    { name: 'Databases', level: 90, icon: <Database size={20} />, color: '#47A248' },
  ];

  const [posts, setPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    fetchPosts().then((result) => setPosts(result.posts.slice(0, 3)));
  }, []);

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* ─── Hero Section ─── */}
      <section
        ref={heroRef}
        className="hero-fade-in hero-padding"
        style={{
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 50%, #0F172A 100%)',
          borderRadius: '24px',
          marginBottom: '64px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle geometric accent */}
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(245,158,11,0.06)', filter: 'blur(60px)' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(59,107,155,0.08)', filter: 'blur(40px)' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '720px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', animation: 'pulse-dot 2s infinite' }} />
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem', fontWeight: 500 }}>
              Accepting new clients
            </span>
          </div>

          <div style={{ minHeight: 'clamp(5.2rem, 12vw, 7.6rem)', display: 'flex', alignItems: 'flex-start' }}>
            <h1
              style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.2rem)',
                fontWeight: 800,
                lineHeight: 1.15,
                color: '#fff',
                fontFamily: 'var(--font-heading)',
                marginBottom: '8px',
              }}
            >
              We Make AI{' '}
              <span style={{ color: '#F59E0B' }}>{typedText}</span>
              <span className="typing-cursor" style={{ color: '#F59E0B', marginLeft: '2px', fontWeight: 100 }}>|</span>
            </h1>
          </div>

          <p
            style={{
              fontSize: '1.15rem',
              color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.7,
              marginBottom: '28px',
              maxWidth: '600px',
            }}
          >
            Enterprise-grade AI and cloud solutions shouldn't be reserved for Fortune 500 budgets. We bring decades of experience from the world's largest companies directly to small and mid-size businesses — so you can harness Generative AI without the complexity or overhead.
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Link
              to="/services"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: '#0F172A',
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '1rem',
                textDecoration: 'none',
                transition: 'transform 0.25s, box-shadow 0.25s',
                boxShadow: '0 4px 15px rgba(245,158,11,0.35)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(245,158,11,0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(245,158,11,0.35)';
              }}
            >
              <Zap size={18} /> See How We Help
            </Link>
            <Link
              to="/contact"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                background: 'transparent',
                color: '#fff',
                borderRadius: '12px',
                fontWeight: 600,
                fontSize: '1rem',
                textDecoration: 'none',
                border: '2px solid rgba(255,255,255,0.2)',
                transition: 'all 0.25s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#F59E0B';
                e.currentTarget.style.color = '#F59E0B';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Mail size={18} /> Let's Talk
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Stats Strip ─── */}
      <AnimatedSection>
        <section
          className="grid-stats"
          style={{
            padding: '48px 0',
            borderTop: '1px solid var(--color-gray-200)',
            borderBottom: '1px solid var(--color-gray-200)',
            margin: '0 0 64px',
          }}
        >
          <StatCard value={5} suffix="" label="AWS Certifications" delay={0} />
          <StatCard value={9} suffix="" label="Professional Certifications" delay={150} />
          <StatCard value={10} suffix="+" label="Years in Enterprise & Gov" delay={300} />
          <StatCard value={100} suffix="%" label="Production-Grade Delivery" delay={450} />
        </section>
      </AnimatedSection>

      {/* ─── Why FretDev ─── */}
      <AnimatedSection>
        <section style={{ marginBottom: '64px' }}>
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              Why FretDev
            </span>
            <h2 className="text-3xl font-bold">Why Growing Businesses Choose FretDev</h2>
          </div>

          <div className="grid-3-col">
            {[
              { icon: <Cloud size={28} />, title: 'Enterprise Architecture, Right-Sized', desc: 'Cloud architecture principles our team refined at Fortune 500 companies — now scaled to fit your business and budget. Cost-optimized, resilient, and built on AWS.', color: '#FF9900' },
              { icon: <Bot size={28} />, title: 'Production-Grade GenAI', desc: 'From RAG pipelines to AI agents, we bring the same Generative AI capabilities powering large enterprises — scoped for your budget and timeline, built for production reliability.', color: '#8B5CF6' },
              { icon: <ShieldCheck size={28} />, title: 'Certified & Battle-Tested', desc: '9 professional certifications including AWS Solutions Architect and GenAI Developer Professional. Our team\'s decade of experience at Fortune 500 companies and government agencies is now available to your business.', color: '#F59E0B' },
            ].map((item, i) => (
              <AnimatedSection key={i} delay={i * 120}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '32px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    height: '100%',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.03)';
                  }}
                >
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '14px',
                      background: `${item.color}12`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.color,
                      marginBottom: '20px',
                    }}
                  >
                    {item.icon}
                  </div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px' }}>{item.title}</h3>
                  <p style={{ color: 'var(--color-gray-500)', lineHeight: 1.7, fontSize: '0.95rem' }}>{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      </AnimatedSection>

      {/* ─── Core Services Preview ─── */}
      <AnimatedSection>
        <section
          className="section-padding-lg"
          style={{
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '24px',
            marginBottom: '64px',
          }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              Our Services
            </span>
            <h2 className="text-3xl font-bold" style={{ marginBottom: '32px' }}>
              Enterprise-Grade Services, Accessible to Every Business
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
              {[
                'AI-powered workflow automation — document processing, data entry, email handling',
                'Custom AI chatbots & knowledge assistants for customers and staff',
                'Cloud setup & optimization on AWS — secure, cost-optimized, no surprise bills',
                'Custom business applications — portals, internal tools, scheduling systems',
                'Data dashboards & reporting your leadership team can act on',
                'AI strategy & roadmapping — know exactly where to invest for the highest ROI',
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <CheckCircle size={20} style={{ color: '#F59E0B', flexShrink: 0, marginTop: '2px' }} />
                  <span style={{ color: 'var(--color-gray-600)', fontSize: '1.05rem', lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>
            <Link
              to="/services"
              className="group"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--color-primary)',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              View all services <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      </AnimatedSection>

      {/* ─── Technologies ─── */}
      <section style={{ padding: '64px 0', marginBottom: '64px' }}>
        <AnimatedSection>
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '6px 16px',
                background: 'rgba(30,58,95,0.08)',
                color: 'var(--color-primary)',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: 600,
                marginBottom: '16px',
              }}
            >
              Technology Stack
            </span>
            <h2 className="text-3xl font-bold">Our Technical Expertise</h2>
          </div>
        </AnimatedSection>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '20px',
          }}
        >
          {skills.map((skill, i) => (
            <SkillBar
              key={skill.name}
              name={skill.name}
              level={skill.level}
              icon={skill.icon}
              color={skill.color}
              delay={i * 80}
            />
          ))}
        </div>
      </section>

      {/* ─── Latest Blog Posts ─── */}
      <section style={{ padding: '64px 0' }}>
        <AnimatedSection>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '48px' }}>
            <div>
              <span
                style={{
                  display: 'inline-block',
                  padding: '6px 16px',
                  background: 'rgba(30,58,95,0.08)',
                  color: 'var(--color-primary)',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  marginBottom: '16px',
                }}
              >
                Insights
              </span>
              <h2 className="text-3xl font-bold">Latest Articles</h2>
            </div>
            <Link
              to="/blog"
              className="group"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'none' }}
            >
              Read all posts <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </AnimatedSection>

        <div className="grid-3-col">
          {posts.map((post, i) => (
            <AnimatedSection key={post.id} delay={i * 120}>
              <Link to={`/blog/${post.slug || post.id}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
                  }}
                >
                  <div style={{ height: '140px', background: post.gradient }} />
                  <div style={{ padding: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--color-gray-400)', marginBottom: '12px' }}>
                      <span>{post.date}</span>
                      <span>{post.readTime} min read</span>
                    </div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '8px', lineHeight: 1.4 }}>{post.title}</h3>
                    <p style={{ color: 'var(--color-gray-500)', fontSize: '0.9rem', lineHeight: 1.6 }}>{post.excerpt}</p>
                  </div>
                </div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ─── CTA Banner ─── */}
      <AnimatedSection>
        <section
          className="section-padding"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
            borderRadius: '24px',
            marginBottom: '32px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(245,158,11,0.08)', filter: 'blur(40px)' }} />
          <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', fontWeight: 800, color: '#fff', marginBottom: '16px', fontFamily: 'var(--font-heading)', position: 'relative' }}>
            Ready to Bring AI Into Your Business?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem', marginBottom: '32px', maxWidth: '500px', marginLeft: 'auto', marginRight: 'auto', position: 'relative' }}>
            Our team has delivered enterprise and government-grade engineering for over a decade. Now that expertise is accessible to growing businesses like yours. Let's talk.
          </p>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: '#0F172A',
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '1rem',
              textDecoration: 'none',
              transition: 'transform 0.25s, box-shadow 0.25s',
              boxShadow: '0 4px 15px rgba(245,158,11,0.35)',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(245,158,11,0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(245,158,11,0.35)';
            }}
          >
            Start a Conversation <ArrowRight size={16} />
          </Link>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Home;
